import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["$active", "$hasChildren", "$hovered", "$responsive", "$depth"];
import { Children, isValidElement, useState } from 'react';
import { Flex } from '../../../components/Flex';
import styled from 'styled-components';
import { View } from '../../../components/View';
import { activeStyles, parentStyles, linkStyles, hoveredStyles } from './SideNavItem.helpers';
import { SideNavContextProvider, useSideNavContext } from './SideNavContext';
import { useDisclosure } from '../../../hooks/useDisclosure';
import { DownIcon, ExternalLinkIcon } from 'foundations-assets';
import { AnimatePresence, m } from 'framer-motion';
import { StatusDot } from '../../../components/StatusDot/StatusDot';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const animationVariants = {
  expanded: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: 0.15
    }
  },
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      duration: 0.15
    }
  }
};
const StyledUl = styled(m.ul).withConfig({
  displayName: "SideNavItem__StyledUl",
  componentId: "sc-1oemyuq-0"
})(["list-style:none;padding-inline-start:0;"]);
const SideNavItemLabel = ({
  children
}) => {
  return /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    children: children
  });
};
const StyledCustomLink = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx("a", Object.assign({}, rest));
}).withConfig({
  displayName: "SideNavItem__StyledCustomLink",
  componentId: "sc-1oemyuq-1"
})(["", " ", " &&{", ";}", " padding-left:", "px;"], linkStyles, ({
  $active
}) => $active ? activeStyles : undefined, ({
  $hovered,
  disabled
}) => $hovered && !disabled ? hoveredStyles : undefined, ({
  $hasChildren
}) => $hasChildren ? parentStyles : undefined, ({
  theme,
  $depth
}) => parseInt(theme.space['300'], 10) * ($depth + 1));
export const SideNavItem = ({
  active = false,
  children,
  count,
  disabled = false,
  defaultOpen,
  external = false,
  href,
  hovered: __hovered,
  iconLeft: IconLeft,
  iconRight: IconRight,
  onBlur,
  onClick,
  onOpenChange,
  open,
  role = 'button',
  title,
  statusDot,
  value: itemValue
}) => {
  if (external) {
    IconRight = ExternalLinkIcon;
  }
  const {
    value: navValue,
    depth
  } = useSideNavContext();
  const [hovered, setHovered] = useState(false);
  const handleMouseEnter = () => {
    setHovered(true);
  };
  const handleMouseLeave = () => {
    setHovered(false);
  };
  const {
    isOpen,
    triggerElementAriaAttributes,
    onToggle
  } = useDisclosure({
    defaultIsOpen: defaultOpen,
    isOpen: open,
    onOpen: () => onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true),
    onClose: () => onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false)
  });
  const isActive = navValue === itemValue ? true : active;
  return /*#__PURE__*/_jsxs("li", {
    children: [/*#__PURE__*/_jsxs(StyledCustomLink, Object.assign({
      $active: isActive,
      $depth: depth,
      $hasChildren: !!children,
      $hovered: hovered,
      $responsive: false,
      "aria-current": true,
      disabled: disabled,
      href: href,
      onBlur: onBlur,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    }, triggerElementAriaAttributes, {
      onClick: () => {
        onToggle();
        onClick === null || onClick === void 0 || onClick();
      },
      role: role,
      children: [/*#__PURE__*/_jsxs(Flex, {
        children: [statusDot && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineEnd: "100",
          children: /*#__PURE__*/_jsx(StatusDot, {
            variant: statusDot
          })
        }), IconLeft && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineEnd: "100",
          children: /*#__PURE__*/_jsx(IconLeft, {
            color: "icon-interactive-default",
            size: "icon-300"
          })
        }), /*#__PURE__*/_jsx(SideNavItemLabel, {
          count: count,
          children: title
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        children: [/*#__PURE__*/_jsx(View, {
          paddingInlineStart: "200",
          children: count
        }), IconRight && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineStart: "200",
          children: /*#__PURE__*/_jsx(IconRight, {
            color: external ? 'icon-primary-default' : 'icon-interactive-default',
            size: "icon-300"
          })
        }), children && /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          paddingInlineStart: "200",
          children: /*#__PURE__*/_jsx(DownIcon, {
            size: "icon-300",
            color: "icon-interactive-default"
          })
        })]
      })]
    })), /*#__PURE__*/_jsx(AnimatePresence, {
      initial: false,
      children: isOpen && children && /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(StyledUl, {
          layout: true,
          variants: animationVariants,
          animate: "expanded",
          initial: "collapsed",
          exit: "collapsed",
          children: Children.map(children, child => {
            if ( /*#__PURE__*/isValidElement(child) && child.type === SideNavItem) {
              return /*#__PURE__*/_jsx(SideNavContextProvider, {
                depth: depth + 1,
                value: navValue,
                children: child
              });
            }
          })
        })
      })
    })]
  });
};